/**
 * "Cadastros em andamento" no topo das listas da equipe e do portal (E189.F2, ADR-0138): cada caso
 * enviado com "Enviar e seguir" que o sistema ainda não concluiu, com a etapa e a contagem ou
 * "Precisa da sua conferência". O caso abre no Conferir da abertura (a rota vem da página). Sem
 * cadastro em andamento, nada aparece.
 */
import { Link } from 'react-router-dom';

import { Spinner } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CadastroEmAndamentoData } from '@/lib/cadastro_sem_espera';
import { cn } from '@/lib/utils';

import { rotuloDaEtapa } from './CadastrandoOCaso';

const t = ptBR.abertura.cadastro;

function Situacao({ cadastro }: { cadastro: CadastroEmAndamentoData }): JSX.Element {
  if (cadastro.andamento.situacao === 'precisa_de_conferencia') {
    return (
      <span className="font-semibold text-warning-700" data-testid="cadastro-parado">
        {t.precisaTitulo}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2" data-testid="cadastro-cadastrando">
      <Spinner size="sm" />
      {rotuloDaEtapa(cadastro.andamento)}
    </span>
  );
}

export function CadastrosEmAndamento({
  cadastros,
  rotaDoCaso,
  className = 'mt-6',
}: {
  cadastros: readonly CadastroEmAndamentoData[];
  rotaDoCaso: (caseId: string) => string;
  /** O espaço em volta, que cada lista acerta com o próprio ritmo. */
  className?: string;
}): JSX.Element | null {
  if (cadastros.length === 0) return null;
  return (
    <section
      aria-labelledby="cadastros-em-andamento"
      data-testid="cadastros-em-andamento"
      className={cn('border border-border bg-surface px-4 py-3', className)}
    >
      <h2 id="cadastros-em-andamento" className="font-display text-[16px] font-semibold text-fg">
        {t.lista.titulo}
      </h2>
      <table className="mt-2 w-full text-left text-body-sm">
        <thead className="text-fg-muted">
          <tr>
            <th className="py-1.5 pr-4 font-normal">{t.lista.colunaCaso}</th>
            <th className="py-1.5 pr-4 font-normal">{t.lista.colunaProcesso}</th>
            <th className="py-1.5 pr-4 font-normal">{t.lista.colunaSituacao}</th>
            <th className="py-1.5" />
          </tr>
        </thead>
        <tbody>
          {cadastros.map((cadastro) => (
            <tr
              key={cadastro.case_id}
              className="border-t border-border"
              data-testid={`cadastro-${cadastro.case_id}`}
            >
              <td className="py-2 pr-4 font-mono text-fg">{cadastro.display_code}</td>
              <td className="py-2 pr-4 text-fg">
                {cadastro.process_number ?? cadastro.pasta ?? ptBR.casos.placeholderEmDash}
              </td>
              <td className="py-2 pr-4 text-fg">
                <Situacao cadastro={cadastro} />
              </td>
              <td className="py-2 text-right">
                <Link
                  to={rotaDoCaso(cadastro.case_id)}
                  aria-label={t.lista.abrirCaso.replace('{codigo}', cadastro.display_code)}
                  className="text-controle text-accent-700 underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  {t.lista.abrir}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
